import React, { useState, useEffect } from 'react';
import { message, Spin, Modal } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { SubTaskInfo } from '../types';
import { taskApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { downloadFile, getFileNameFromUrl } from '../utils/electronDownload';

interface TaskStats {
  create_fail: number;
  queuing: number;
  processing: number;
  fail: number;
  success: number;
}

interface TaskDetail extends SubTaskInfo {
  cloud_srv_name?: string;
}

const TaskQueuePageNexus: React.FC = () => {
  const { isLoggedIn, token, user } = useAuth();
  const [tasks, setTasks] = useState<SubTaskInfo[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<SubTaskInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [taskStats, setTaskStats] = useState<TaskStats>({
    create_fail: 0,
    queuing: 0,
    processing: 0,
    fail: 0,
    success: 0
  });

  const refreshInterval = parseInt(import.meta.env.VITE_SERVER_REFRESH_INTERVAL || '30') * 1000;

  // 状态颜色映射
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'processing':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'queuing':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'fail':
      case 'create_fail':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  // 状态文本映射
  const getStatusText = (status: string) => {
    const textMap = {
      created: '已创建',
      create_fail: '创建失败',
      queuing: '排队中',
      processing: '执行中',
      fail: '执行失败',
      success: '执行成功'
    };
    return textMap[status as keyof typeof textMap] || '未知';
  };

  // 状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined className="text-green-600" style={{ fontSize: '20px' }} />;
      case 'processing':
        return <LoadingOutlined className="text-blue-600" style={{ fontSize: '20px' }} />;
      case 'queuing':
        return <ClockCircleOutlined className="text-orange-600" style={{ fontSize: '20px' }} />;
      case 'fail':
      case 'create_fail':
        return <CloseCircleOutlined className="text-red-600" style={{ fontSize: '20px' }} />;
      default:
        return <ClockCircleOutlined className="text-gray-600" style={{ fontSize: '20px' }} />;
    }
  };

  // 解析返回结果中的URL（支持 JSON 格式和逗号分隔字符串）
  const parseResultUrls = (resultData: any): string[] => {
    if (!resultData) return [];
    
    try {
      let data = resultData;
      const resultStr = typeof resultData === 'string' ? resultData : '';
      
      // 如果是字符串，先检查是否直接是 URL 或逗号分隔的 URL 列表
      if (resultStr) {
        // 检查是否以 http 开头（直接是 URL 或逗号分隔的 URL）
        if (resultStr.trim().startsWith('http')) {
          const urls = resultStr.split(',').map((u: string) => u.trim()).filter(Boolean);
          if (urls.length > 0) {
            return urls;
          }
        }
        
        // 尝试 JSON 解析
        try {
          data = JSON.parse(resultStr);
        } catch {
          // JSON 解析失败，返回空
          return [];
        }
      }
      
      // 处理 JSON 对象中的 urls 数组
      if (data && data.urls && Array.isArray(data.urls)) {
        return data.urls.map((url: string) => url.trim().replace(/^["']|["']$/g, ''));
      }
    } catch (error) {
      console.warn('Failed to parse result URLs:', error);
    }
    
    return [];
  };

  // 下载任务结果文件，并在 Electron 中触发保存对话框。
  const handleDownloadResult = async (url: string, index: number): Promise<void> => {
    const suggestedName = getFileNameFromUrl(url, `task-result-${index + 1}`);
    const messageKey = `download-result-${index}`;

    try {
      message.loading({ content: '正在下载文件...', key: messageKey, duration: 0 });
      const success = await downloadFile(url, suggestedName);

      if (!success) {
        message.warning({ content: '已取消下载或下载失败', key: messageKey });
        return;
      }

      message.success({ content: '下载完成', key: messageKey });
      setDetailModalVisible(false);
    } catch (error) {
      console.error('下载任务结果失败:', error);
      message.error({ content: '下载失败，请稍后重试', key: messageKey });
    }
  };

  // 计算任务统计
  const calculateTaskStats = (taskList: SubTaskInfo[]): TaskStats => {
    const stats: TaskStats = {
      create_fail: 0,
      queuing: 0,
      processing: 0,
      fail: 0,
      success: 0
    };

    taskList.forEach(task => {
      if (task.status in stats) {
        stats[task.status as keyof TaskStats]++;
      }
    });

    return stats;
  };

  // 加载任务数据
  const loadTasks = async () => {
    try {
      setLoading(true);
      
      if (!user?.user_id) {
        console.warn('用户未登录，无法加载任务');
        setTasks([]);
        setFilteredTasks([]);
        setLoading(false);
        return;
      }
      
      const response = await taskApi.getSubTasks(user.user_id);
      
      if (response.success && response.data && response.data.tasks) {
        const tasksArray = response.data.tasks;
        const sortedTasks = tasksArray.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setTasks(sortedTasks);
        setFilteredTasks(sortedTasks);
        setTaskStats(calculateTaskStats(sortedTasks));
      } else {
        console.warn('Failed to load tasks from API');
        setTasks([]);
        setFilteredTasks([]);
        setTaskStats({
          create_fail: 0,
          queuing: 0,
          processing: 0,
          fail: 0,
          success: 0
        });
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
      message.error('加载任务列表失败');
      setTasks([]);
      setFilteredTasks([]);
      setTaskStats({
        create_fail: 0,
        queuing: 0,
        processing: 0,
        fail: 0,
        success: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // 过滤任务（resetPage=true 时才重置到第1页，仅搜索/筛选条件变化时使用）
  const filterTasks = (resetPage = false) => {
    let filtered = tasks;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    if (searchText) {
      filtered = filtered.filter(task =>
        (task.task_name && task.task_name.toLowerCase().includes(searchText.toLowerCase())) ||
        task.task_id.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    setFilteredTasks(filtered);
    if (resetPage) setCurrentPage(1);
  };

  // 查看任务详情
  const handleViewDetail = async (task: SubTaskInfo) => {
    try {
      let taskDetail: TaskDetail = { ...task };
      
      if (task.cloud_srv_id) {
        try {
          const serverResponse = await taskApi.getCloudServer(task.cloud_srv_id);
          if (serverResponse.success && serverResponse.data) {
            taskDetail.cloud_srv_name = serverResponse.data.srv_name;
          }
        } catch (error) {
          console.warn('Failed to load server name:', error);
        }
      }
      
      setSelectedTask(taskDetail);
      setDetailModalVisible(true);
    } catch (error) {
      console.error('Error loading task detail:', error);
      message.error('加载任务详情失败');
    }
  };

  // 自动刷新
  useEffect(() => {
    if (autoRefresh && isLoggedIn && token) {
      const interval = setInterval(() => {
        loadTasks();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, isLoggedIn, token]);

  // 组件挂载时加载数据
  useEffect(() => {
    if (isLoggedIn && token) {
      loadTasks();
    }
  }, [isLoggedIn, token]);

  // 搜索/筛选条件变化时重置页码；任务列表刷新时保留当前页
  const prevSearchRef = React.useRef(searchText);
  const prevStatusRef = React.useRef(statusFilter);
  useEffect(() => {
    const conditionChanged = prevSearchRef.current !== searchText || prevStatusRef.current !== statusFilter;
    prevSearchRef.current = searchText;
    prevStatusRef.current = statusFilter;
    filterTasks(conditionChanged);
  }, [searchText, statusFilter, tasks]);

  // 分页计算
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTasks = filteredTasks.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredTasks.length / pageSize);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* 页面标题和操作按钮 */}
        <div className="mb-8 bg-white rounded-2xl p-6 border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">执行任务队列</h1>
              <p className="text-lg text-gray-600">实时监控所有任务的执行状态</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  autoRefresh
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-indigo-200'
                }`}
              >
                {autoRefresh ? '停止自动刷新' : '开启自动刷新'}
              </button>
              <button
                onClick={loadTasks}
                disabled={loading}
                className="px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-xl font-medium hover:border-indigo-200 transition-all flex items-center space-x-2"
              >
                <ReloadOutlined style={{ fontSize: '18px' }} />
                <span>手动刷新</span>
              </button>
            </div>
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">排队中</span>
              <ClockCircleOutlined style={{ fontSize: '24px', color: '#F59E0B' }} />
            </div>
            <div className="text-3xl font-bold text-orange-600">{taskStats.queuing}</div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">执行中</span>
              <LoadingOutlined style={{ fontSize: '24px', color: '#3B82F6' }} />
            </div>
            <div className="text-3xl font-bold text-blue-600">{taskStats.processing}</div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">执行成功</span>
              <CheckCircleOutlined style={{ fontSize: '24px', color: '#10B981' }} />
            </div>
            <div className="text-3xl font-bold text-green-600">{taskStats.success}</div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">执行失败</span>
              <ExclamationCircleOutlined style={{ fontSize: '24px', color: '#EF4444' }} />
            </div>
            <div className="text-3xl font-bold text-red-600">{taskStats.fail}</div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">创建失败</span>
              <CloseCircleOutlined style={{ fontSize: '24px', color: '#EF4444' }} />
            </div>
            <div className="text-3xl font-bold text-red-600">{taskStats.create_fail}</div>
          </div>
        </div>

        {/* 搜索和筛选 */}
        <div className="mb-6 bg-white rounded-2xl p-6 border border-gray-200">
          <div className="flex flex-wrap gap-4 items-center">
            <input
              type="text"
              placeholder="搜索任务名称或ID"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="flex-1 min-w-[300px] px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-6 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
            >
              <option value="all">全部状态</option>
              <option value="created">已创建</option>
              <option value="create_fail">创建失败</option>
              <option value="queuing">排队中</option>
              <option value="processing">执行中</option>
              <option value="fail">执行失败</option>
              <option value="success">执行成功</option>
            </select>
            <span className="text-gray-600">
              共 {filteredTasks.length} 个任务
            </span>
          </div>
        </div>

        {/* 任务列表 */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Spin size="large" />
          </div>
        ) : paginatedTasks.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <ClockCircleOutlined style={{ fontSize: '64px', color: '#9CA3AF' }} />
            <p className="mt-4 text-gray-500 text-lg">暂无任务记录</p>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              {paginatedTasks.map((task) => (
                <div
                  key={task.task_id}
                  className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        {getStatusIcon(task.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {task.task_name || '未命名任务'}
                        </h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p className="font-mono text-xs">{task.task_id}</p>
                          <p>创建时间: {dayjs(task.created_at).format('YYYY-MM-DD HH:mm:ss')}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 ml-4">
                      <span className={`px-4 py-2 rounded-xl text-sm font-medium border ${getStatusColor(task.status)}`}>
                        {getStatusText(task.status)}
                      </span>
                      <button
                        onClick={() => handleViewDetail(task)}
                        className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all flex items-center space-x-2"
                      >
                        <EyeOutlined style={{ fontSize: '18px' }} />
                        <span>查看任务</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center bg-white rounded-2xl p-4 border border-gray-200">
                <div className="text-sm text-gray-600">
                  第 {startIndex + 1}-{Math.min(endIndex, filteredTasks.length)} 条，共 {filteredTasks.length} 条
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl hover:border-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    上一页
                  </button>
                  <span className="px-4 py-2 text-gray-700">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl hover:border-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    下一页
                  </button>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                  >
                    <option value={10}>10条/页</option>
                    <option value={20}>20条/页</option>
                    <option value={50}>50条/页</option>
                    <option value={100}>100条/页</option>
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 任务详情弹窗 */}
      <Modal
        title="任务详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedTask && (
          <div className="space-y-4">
            <div>
              <span className="font-semibold text-gray-700">任务ID: </span>
              <span className="font-mono text-sm text-gray-600">{selectedTask.task_id}</span>
            </div>
            
            <div>
              <span className="font-semibold text-gray-700">任务名称: </span>
              <span className="text-gray-600">{selectedTask.task_name || '未命名任务'}</span>
            </div>
            
            <div>
              <span className="font-semibold text-gray-700">任务状态: </span>
              <span className={`px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(selectedTask.status)}`}>
                {getStatusText(selectedTask.status)}
              </span>
            </div>
            
            <div>
              <span className="font-semibold text-gray-700">创建时间: </span>
              <span className="text-gray-600">{dayjs(selectedTask.created_at).format('YYYY-MM-DD HH:mm:ss')}</span>
            </div>
            
            <div>
              <span className="font-semibold text-gray-700">工作流ID: </span>
              <span className="text-gray-600">{selectedTask.wf_id}</span>
            </div>
            
            <div>
              <span className="font-semibold text-gray-700">分配的云服务器名称: </span>
              <span className="text-gray-600">{selectedTask.cloud_srv_name || '已释放'}</span>
            </div>
            
            <div>
              <span className="font-semibold text-gray-700">返回结果: </span>
              {selectedTask.result_info ? (
                <div className="mt-2">
                  {(() => {
                    const urls = parseResultUrls(selectedTask.result_info);
                    if (urls.length > 0) {
                      return (
                        <div className="space-y-2">
                          {urls.map((url, index) => (
                            <div key={index}>
                              <button
                                type="button"
                                onClick={() => void handleDownloadResult(url, index)}
                                className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer"
                              >
                                点击下载-{index + 1}
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    } else {
                      return (
                        <pre className="bg-gray-50 p-4 rounded-lg text-xs overflow-auto max-h-48">
                          {typeof selectedTask.result_info === 'string' 
                            ? selectedTask.result_info 
                            : JSON.stringify(selectedTask.result_info, null, 2)}
                        </pre>
                      );
                    }
                  })()}
                </div>
              ) : (
                <span className="text-gray-400">无返回信息</span>
              )}
            </div>
            
            {(selectedTask.status === 'create_fail' || selectedTask.status === 'fail') && (
              <div>
                <span className="font-semibold text-gray-700">错误信息: </span>
                {selectedTask.error_message ? (
                  <span className="text-red-600">{selectedTask.error_message}</span>
                ) : (
                  <span className="text-gray-400">无错误信息</span>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TaskQueuePageNexus;
